/** @jsx isolateComponent */

import { css } from '@emotion/react';

import { Colors, createUniqueCustomProperties } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../../../constants';

import type {
  NavItemProduct2,
  NavItemVariantB,
  NavItemVariantC,
} from '../../../../constants/links';
import { MegaMenuFooter } from '../MegaMenuFooter';
import { MegaMenuProductLeftV2 } from './MegaMenuProductLeftV2';
import { MegaMenuSubmenu } from '../MegaMenuSubmenu';
import { Promo } from '../Promo';
import ProductMegaMenuRight from './MegaMenuProductRight';

const Vars = createUniqueCustomProperties('MegaMenu', [
  'footerWidth',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'leftBackgroundColor',
  'rightBackgroundColor',
]);

type Props = {
  id: string;
  /**
   * Whether or not the mega-menu is open
   */
  isMenuOpen: boolean;
  /**
   * Navigation menu to be rendered based on `navigationItem`'s `variant`.
   */
  navigationItem: NavItemVariantB | NavItemVariantC | NavItemProduct2;
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('MegaMenu', {
  root: (isMenuOpen: boolean) => [
    css`
      ${Vars.footerWidth}: 90%;
      ${Vars.gridTemplateAreas}: 'left-padding left-content right-content right-padding'
      'left-padding footer right-content right-padding';
      ${Vars.gridTemplateColumns}: minmax(20px, 1fr) minmax(613.33px, 780px) minmax(306.66px, 380px) minmax(20px, 1fr);
      ${Vars.leftBackgroundColor}: ${Colors.neutral100};
      ${Vars.rightBackgroundColor}: ${Colors.neutral200};

      &[data-layout='product-2'] {
        ${Vars.footerWidth}: 100%;
        ${Vars.gridTemplateAreas}: 'left-padding left-content right-content right-padding'
        'lower-left footer footer footer';
        ${Vars.gridTemplateColumns}: minmax(20px, 1fr) minmax(613.33px, 810px) minmax(306.66px, 350px) minmax(20px, 1fr);
        ${Vars.leftBackgroundColor}: ${Colors.neutral100};
        ${Vars.rightBackgroundColor}: ${Colors.neutral200};
      }
    `,
    css({
      backgroundColor: `var(${Vars.leftBackgroundColor})`,
      border: `1px solid ${Colors.neutral400}`,
      position: 'absolute',
      top: UNIVERSAL_TOPBAR_HEIGHT,
      left: 0,
      right: 0,
      minHeight: '100%',
      width: '100%',
      display: `${isMenuOpen ? 'block' : 'none'}`,
    }),
  ],
  megaMenuWrapper: css({
    display: 'grid',
    gridTemplateColumns: `var(${Vars.gridTemplateColumns})`,
    gridTemplateRows: '1fr auto',
    gridTemplateAreas: `var(${Vars.gridTemplateAreas})`,
    minHeight: 410,
  }),
  submenu: css({
    gridArea: 'left-content',
  }),
  submenuLinksContainer: (layout: Props['navigationItem']['variant']) =>
    css({
      padding: layout === 'product-2' ? '33px 0 29px' : '30px 15px 40px 0',
    }),
  promotionsContainer: (layout: Props['navigationItem']['variant']) =>
    css({
      background: `var(${Vars.rightBackgroundColor})`,
      padding: layout === 'product-2' ? '33px 0 40px 40px' : '40px 0 40px 40px',
      gridArea: 'right-content',
      display: 'flex',
      alignItems: 'normal',
    }),

  footerContainer: css({
    boxShadow: `inset 0 1px ${Colors.neutral300}`,
    gridArea: 'footer',
    width: `var(${Vars.footerWidth})`,
    padding: '20px 2px',
    backgroundColor: Colors.neutral100,
  }),
  lowerLeftPadding: {
    gridArea: 'lower-left',
    backgroundColor: Colors.neutral100,
    boxShadow: `inset 0 1px ${Colors.neutral300}`,
  },
  rightColumn: css({
    gridArea: 'right-padding',
    backgroundColor: `var(${Vars.rightBackgroundColor})`,
  }),
});

/**
 * @private
 * Component that determines which menu layout to render based on `navigationItem`'s `variant`.
 */
const MegaMenu = ({ id, isMenuOpen, navigationItem, onCloseMenu }: Props) => {
  const Wrapper = ({
    leftContent,
    rightContent,
  }: {
    leftContent: React.ReactNode;
    rightContent: React.ReactNode;
  }) => {
    return (
      <div
        css={Styles.root(isMenuOpen)}
        data-layout={navigationItem.variant}
        id={id}
      >
        <div css={Styles.megaMenuWrapper}>
          <div css={Styles.submenu}>
            <div css={Styles.submenuLinksContainer(navigationItem.variant)}>
              {leftContent}
            </div>
          </div>
          <div css={Styles.promotionsContainer(navigationItem.variant)}>
            {rightContent}
          </div>
          <div css={Styles.footerContainer}>
            <MegaMenuFooter
              links={navigationItem.bottomLinks}
              onCloseMenu={onCloseMenu}
            />
          </div>
          <div css={Styles.lowerLeftPadding} />
          <div css={Styles.rightColumn} />
        </div>
      </div>
    );
  };

  switch (navigationItem.variant) {
    case 'product-2':
      return (
        <Wrapper
          leftContent={
            <MegaMenuProductLeftV2
              heading={navigationItem.heroArea.heading}
              subheading={navigationItem.heroArea.subheading}
              submenu={navigationItem.heroArea.submenu}
            />
          }
          rightContent={
            <ProductMegaMenuRight
              header={navigationItem.promoArea.header}
              submenu={navigationItem.promoArea.submenu}
              onCloseMenu={onCloseMenu}
            />
          }
        />
      );
    case 'variant-b':
    case 'variant-c':
      return (
        <Wrapper
          leftContent={
            <MegaMenuSubmenu
              links={navigationItem.submenu}
              onCloseMenu={onCloseMenu}
              variant={navigationItem.variant}
            />
          }
          rightContent={
            <Promo promo={navigationItem.promoArea} onCloseMenu={onCloseMenu} />
          }
        />
      );
    default:
      return null;
  }
};

export default MegaMenu;
